import * as React from 'react';
import {
    Alert, Avatar, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { getUserAggregatedInfo } from '../../Api';
import AggregateUserInfoDto from '../../models/dtos/AggregateUserInfoDto';
import { BetupFeedPlayerDto } from '../../models/dtos/BetupFeedPostDto';
import { userPurposeLabel } from '../../models/userPurpose';
import { formatCount, formatFeedDate } from './BetupFeedShared';

/** What the list row knew about the player; the drawer loads the rest (the user page's aggregated info). */
export interface SubscriberDrawerEntry {
    userId: number;
    player: BetupFeedPlayerDto | null;
    /** Current subscription facts (the Subscribers tab). */
    subscribedAt?: string | null;
    notificationsEnabled?: boolean | null;
    channelsCount?: number | null;
    /** The log row (the Activity tab). */
    event?: { subscribed: boolean; at: string; backfilled: boolean } | null;
    subscribedNow?: boolean | null;
}

/** "3 days ago" / "in 2 hours" style distance of an ISO time from now. */
export function relativeTime(value?: string | null): string {
    if (!value) return '';
    const ms = new Date(value).getTime() - Date.now();
    if (Number.isNaN(ms)) return '';
    const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
        ['year', 365 * 86400e3], ['month', 30 * 86400e3], ['day', 86400e3], ['hour', 3600e3], ['minute', 60e3],
    ];
    const format = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    for (const [unit, size] of units) {
        if (Math.abs(ms) >= size) return format.format(Math.round(ms / size), unit);
    }
    return 'just now';
}

const languageNames = (() => {
    try { return new Intl.DisplayNames(['en'], { type: 'language' }); } catch { return null; }
})();

/** qbet_user.osType ("ios", "android", …) for people. */
export function platformLabel(os?: string | null): string {
    if (!os) return 'Unknown';
    const known: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web' };
    return known[os.toLowerCase()] ?? os;
}

export function languageLabel(code?: string | null): string {
    if (!code) return 'Unknown';
    try { return languageNames?.of(code) ?? code; } catch { return code; }
}

/**
 * A subscriber's card beside the lists: who they are, their subscription, and the user page's key facts. ↑ / ↓ (or
 * the keys) walk the list; "Open user page" leaves for the full profile.
 */
export default function ChannelSubscriberDrawer({ entry, position, onClose, onPrevious, onNext }: {
    entry: SubscriberDrawerEntry | null;
    /** "3 of 120" — null when not known. */
    position: string | null;
    onClose: () => void;
    onPrevious: (() => void) | null;
    onNext: (() => void) | null;
}) {
    const [info, setInfo] = React.useState<AggregateUserInfoDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const userId = entry?.userId ?? null;

    React.useEffect(() => {
        if (userId == null) return;
        let cancelled = false;
        setInfo(null);
        setFailed(false);
        setLoading(true);
        getUserAggregatedInfo(userId)
            .then(result => { if (!cancelled) { setInfo(result); setFailed(result == null); } })
            .catch(() => { if (!cancelled) setFailed(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [userId]);

    React.useEffect(() => {
        if (entry == null) return;
        const onKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
            if (event.key === 'Escape') { onClose(); return; }
            if ((event.key === 'ArrowDown' || event.key === 'j') && onNext) { event.preventDefault(); onNext(); }
            if ((event.key === 'ArrowUp' || event.key === 'k') && onPrevious) { event.preventDefault(); onPrevious(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [entry, onNext, onPrevious, onClose]);

    const general = info?.general;
    const name = entry?.player?.name || general?.name || (entry ? `#${entry.userId}` : '');
    const photo = entry?.player?.photo_url ?? general?.photoUrl ?? undefined;
    const subscribedNow = entry?.subscribedNow ?? (entry?.subscribedAt != null ? true : null);

    return (
        <Drawer anchor="right" open={entry != null} onClose={onClose} variant="persistent"
            PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, boxShadow: 6 } }}>
            {entry && (
                <Stack sx={{ height: '100%' }}>
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <Tooltip title="Previous (↑)">
                            <span><IconButton size="small" disabled={!onPrevious} onClick={() => onPrevious?.()}><KeyboardArrowUpIcon /></IconButton></span>
                        </Tooltip>
                        <Tooltip title="Next (↓)">
                            <span><IconButton size="small" disabled={!onNext} onClick={() => onNext?.()}><KeyboardArrowDownIcon /></IconButton></span>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{position ?? ''}</Typography>
                        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
                    </Stack>

                    <Box sx={{ overflowY: 'auto', flex: 1, p: 2.5 }}>
                        <Stack direction="row" gap={2} alignItems="center">
                            <Avatar src={photo} alt={name} sx={{ width: 64, height: 64, fontSize: 28 }}>
                                {name.slice(0, 1).toUpperCase()}
                            </Avatar>
                            <Stack sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ lineHeight: 1.2, wordBreak: 'break-word' }}>{name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    #{entry.userId}
                                    {(entry.player?.level ?? info?.balance?.level) != null ? ` · Level ${entry.player?.level ?? info?.balance?.level}` : ''}
                                </Typography>
                                {(general?.country?.name || entry.player?.country_iso) && (
                                    <Stack direction="row" gap={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                                        {general?.country?.icon && <img src={general.country.icon} alt="" width={18} height={12} style={{ objectFit: 'cover', borderRadius: 2 }} />}
                                        <Typography variant="body2" color="text.secondary">{general?.country?.name ?? entry.player?.country_iso}</Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>

                        <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 2 }}>
                            {subscribedNow === true && <Chip size="small" color="success" label="Subscribed now" />}
                            {subscribedNow === false && <Chip size="small" variant="outlined" label="Not subscribed now" />}
                            {entry.notificationsEnabled === true && <Chip size="small" variant="outlined" color="success" label="Push on" />}
                            {entry.notificationsEnabled === false && <Chip size="small" variant="outlined" color="warning" label="Muted" />}
                            {general?.purpose && <Chip size="small" variant="outlined" label={userPurposeLabel(general.purpose)} />}
                        </Stack>

                        <Section title="Subscription">
                            {entry.subscribedAt && <Fact label="Subscribed" value={formatFeedDate(entry.subscribedAt)} hint={relativeTime(entry.subscribedAt)} />}
                            {entry.event && (
                                <Fact
                                    label={entry.event.subscribed ? 'Subscribed' : 'Unsubscribed'}
                                    value={formatFeedDate(entry.event.at)}
                                    hint={entry.event.backfilled ? 'subscribed before history was recorded' : relativeTime(entry.event.at)}
                                />
                            )}
                            {entry.channelsCount != null && <Fact label="Channels followed" value={formatCount(entry.channelsCount)} />}
                        </Section>

                        {loading && <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>}
                        {failed && !loading && <Alert severity="warning" sx={{ mt: 2 }}>Couldn’t load the player’s details.</Alert>}
                        {info && (
                            <>
                                <Section title="Player">
                                    <Fact label="Registered" value={formatFeedDate(general?.createdAt)} hint={relativeTime(general?.createdAt)} />
                                    {info.userTagsInfo?.lastActiveAt && (
                                        <Fact label="Last active" value={formatFeedDate(info.userTagsInfo.lastActiveAt)} hint={relativeTime(info.userTagsInfo.lastActiveAt)} />
                                    )}
                                    <Fact label="Platform" value={[platformLabel(info.deviceInfo?.osType ?? general?.osType), info.deviceInfo?.buildVersion].filter(Boolean).join(' · ')} />
                                    <Fact label="Language" value={languageLabel(info.advanced?.lang ?? general?.language)} />
                                    {info.advanced?.timezone && <Fact label="Time zone" value={info.advanced.timezone} />}
                                    {general?.email && <Fact label="Email" value={general.email} />}
                                </Section>
                                <Section title="Game">
                                    <Fact label="Balance" value={`${formatCount(info.balance?.moneyInfo)} betcoins`} />
                                    <Fact label="Level / XP" value={`${info.balance?.level ?? '—'} / ${formatCount(info.balance?.experience)}`} />
                                    <Fact label="Rank (all / month / day)"
                                        value={[info.ranks?.rank, info.ranks?.rankMonth, info.ranks?.rankDay].map(r => r ? `#${formatCount(r)}` : '—').join(' / ')} />
                                    <Fact label="Purchases" value={`${formatCount(info.activity?.purchasesCount)} · $${(info.activity?.totalPurchased ?? 0).toFixed(2)}`} />
                                    <Fact label="Videos watched" value={formatCount(info.activity?.videosWatched)} />
                                </Section>
                                {(general?.tags?.length ?? 0) > 0 && (
                                    <Section title="Tags">
                                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                                            {general!.tags.map(tag => <Chip key={tag} size="small" variant="outlined" label={tag} />)}
                                        </Stack>
                                    </Section>
                                )}
                            </>
                        )}
                    </Box>

                    <Stack direction="row" gap={1} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Button variant="contained" component={RouterLink} to={`/users/${entry.userId}`} sx={{ flex: 1 }}>
                            Open user page
                        </Button>
                        <Tooltip title="Open in a new tab">
                            <IconButton component="a" href={`/users/${entry.userId}`} target="_blank" rel="noopener">
                                <OpenInNewIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            )}
        </Drawer>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Box sx={{ mt: 2.5 }}>
            <Typography variant="overline" color="text.secondary">{title}</Typography>
            <Divider sx={{ mb: 1 }} />
            <Stack gap={0.75}>{children}</Stack>
        </Box>
    );
}

function Fact({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
    return (
        <Stack direction="row" gap={2} alignItems="baseline">
            <Typography variant="body2" color="text.secondary" sx={{ width: 150, flexShrink: 0 }}>{label}</Typography>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
                {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
            </Box>
        </Stack>
    );
}
